import { Avatar, type AvatarShape, type AvatarSize } from "@cntrl/ui";
import { useFriends } from "../../app/hooks/useFriends.js";
import { useAccount } from "../../app/hooks/useAccount.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { useBandContextOrNull } from "../../app/bandContext.js";

/** The three fields of a lineup slot the act photo reads — kept structural so both the editor's
 *  `LineupSlotDto` draft and the preview's server slot satisfy it without a cast. */
export interface ActPhotoSlot {
  friendBandId?: string | null;
  spotifyImageUrl?: string | null;
  /** The band's picture id as the server resolved it on read (ART-297) — what lets a registered band that is
   *  neither us nor a friend draw its photo. */
  friendBandAvatarMediaId?: string | null;
}

/**
 * Where an act's photo comes from — resolved as DATA, never a URL, because the URL is fetched per chip by
 * {@link ActAvatar}. Two genuinely different sources:
 *
 *  • **A band on Artist CNTRL** (our own act, or a friend on the bill) — served by the tenant-less global
 *    avatar route (`useAvatarUrl("band", …)`), the ONE band-photo path across the app. It needs no reach
 *    into the band, which is exactly why it works for a friend act the viewer has no seat on — the old
 *    server-resolved `photoUrl` was null on every lane with no public CDN, so those acts rendered initials.
 *  • **A Spotify act** — the artwork denormalised onto the slot at pick time, right for a VENDOR where a
 *    round trip per row costs quota.
 */
export type ActPhotoSource =
  | { kind: "band"; bandId: string; mediaId: string | null }
  | { kind: "vendor"; url: string | null };

/**
 * Resolve each act to its photo SOURCE (not a URL). Our own band and every accepted friend contribute their
 * `avatarMediaId`; a slot's band is looked up against that map, and a slot with no band is a vendor act.
 *
 * Pure data — no hooks per act — so it is safe to call inside a lineup `.map()`; {@link ActAvatar} is what
 * hosts the resolution hook, one per chip. Resolved at RENDER time rather than stored, so a band that
 * changes its photo changes it everywhere.
 */
export function useActPhotoSource() {
  const friends = useFriends();
  // Our own band's media id comes from `settings/account` (band:read), the same field a friend's resolves
  // from (`bands.avatar_media_id`), so one act looks the same to everyone. It is the MOUNTED band's account,
  // so it is keyed by the mounted band — not by whoever owns the show: in the guest editor the show is the
  // HOST's, and keying by owner drew the guest's own photo on the host's act.
  const mountedBandId = useBandContextOrNull()?.bandId;
  const account = useAccount();

  const mediaByBand = new Map<string, string | null>();
  if (mountedBandId) mediaByBand.set(mountedBandId, account.data?.avatarMediaId ?? null);
  for (const f of friends.data?.active ?? []) {
    if (f.friendBandId) mediaByBand.set(f.friendBandId, f.avatarMediaId ?? null);
  }

  // Our own band and our friends come from their live reads (so a photo changed this session shows at once);
  // any other registered band falls back to the id the server put on the slot.
  return (slot: ActPhotoSlot): ActPhotoSource =>
    slot.friendBandId
      ? {
          kind: "band",
          bandId: slot.friendBandId,
          mediaId: mediaByBand.has(slot.friendBandId)
            ? (mediaByBand.get(slot.friendBandId) ?? null)
            : (slot.friendBandAvatarMediaId ?? null),
        }
      : { kind: "vendor", url: slot.spotifyImageUrl ?? null };
}

/**
 * One act's avatar. A band act resolves through the global avatar route; a Spotify act uses its
 * denormalised artwork. Its own component so each chip in a lineup can host `useAvatarUrl`. Initials are the
 * designed empty look either way.
 */
export function ActAvatar({
  source,
  initials,
  size,
  shape,
}: {
  source: ActPhotoSource;
  initials: string;
  size?: AvatarSize;
  shape?: AvatarShape;
}) {
  // Called unconditionally; a vendor act passes nulls, which the hook resolves to nothing (its `url` wins).
  const bandUrl = useAvatarUrl(
    "band",
    source.kind === "band" ? source.bandId : null,
    source.kind === "band" ? source.mediaId : null,
  );
  const src = source.kind === "band" ? bandUrl : source.url;
  return <Avatar initials={initials} src={src ?? null} size={size} shape={shape} />;
}

/** True when this lineup slot is the band whose show this is — OUR act on the bill. Shared for the same
 *  reason the resolver is: the editor marks it and refuses to remove it, and the preview must not offer to
 *  share the show with ourselves (ART-90). One predicate, not two spellings of it. */
export function isOwnAct(slot: ActPhotoSlot, ownerBandId: string | undefined): boolean {
  return !!slot.friendBandId && !!ownerBandId && slot.friendBandId === ownerBandId;
}
